import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, LogIn, UserPlus, Shield, KeyRound } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useToast } from "@/hooks/use-toast";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { Globe } from "lucide-react";
import { z } from "zod";
import appLogo from "@/assets/eyecontrol-icon-white.png";

const authSchema = (t: any) => z.object({
  email: z.string().email(t("invalidEmail")),
  password: z.string().min(6, t("passwordMinLength"))
});

const resetSchema = (t: any) => z.object({
  email: z.string().email(t("invalidEmail"))
});

export default function Auth() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, dir, isRTL, lang, setLanguage } = useCaregiverTranslation();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{email?: string;password?: string;}>({});
  const [needsMfa, setNeedsMfa] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaFactorId, setMfaFactorId] = useState("");
  const [mfaChallengeId, setMfaChallengeId] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Responsive sizing to avoid internal scrolling/cropping on shorter laptop viewports
  const controlHeight = "clamp(2.5rem, 6vh, 3rem)";
  const cardPadding = "clamp(1rem, 3vh, 1.5rem)";
  const logoBoxSize = "clamp(3rem, 9vh, 5rem)";
  const logoIconSize = "clamp(2.25rem, 6.5vh, 3.5rem)";
  const titleFontSize = "clamp(1.375rem, 3.6vh, 1.875rem)";
  const subtitleFontSize = "clamp(0.875rem, 2.2vh, 1rem)";
  const sectionGap = "clamp(0.625rem, 1.8vh, 1rem)";
  const dividerMargin = "clamp(0.5rem, 1.8vh, 1rem)";

  useEffect(() => {
    // Check for reset token in URL
    const token = searchParams.get('token');
    const isReset = searchParams.get('reset');

    if (isReset && token) {
      // Verify the reset token
      verifyResetToken(token);
      return;
    }

    // Check for OAuth error in URL
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const error = hashParams.get('error');
    const errorDescription = hashParams.get('error_description');

    if (error) {
      // OAuth error detected
      toast({
        title: t("authError"),
        description: errorDescription || t("authError"),
        variant: "destructive"
      });
      // Clear the hash
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }

    // Check for access_token in URL (OAuth callback)
    // OAuth callback handling is done via onAuthStateChange listener

    // Listen for auth changes FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {

      if (event === "SIGNED_IN" && session) {
        // Check if this is an OAuth provider (Google users don't need custom verification)
        const provider = session.user.app_metadata?.provider;
        const isOAuthUser = provider && provider !== 'email';

        // Defer Supabase calls to avoid deadlock
        setTimeout(async () => {
          // For OAuth users, skip our custom verification (Google already verified)
          if (isOAuthUser) {
            const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
            if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
              setNeedsMfa(true);
              await startMfaChallenge();
            } else {
              navigate("/");
            }
            return;
          }

          // For email users, treat platform-confirmed email as verified (fallback)
          const isEmailConfirmed = Boolean(
            session.user.email_confirmed_at || (session.user as any).confirmed_at
          );

          if (!isEmailConfirmed) {
            try {
              const { data: verificationData, error: verificationError } = await supabase.functions.invoke(
                'custom-auth-email',
                {
                  body: { action: 'check_email_verified', email: session.user.email }
                }
              );

              if (verificationError || !verificationData?.verified) {
                // User hasn't verified - sign them out immediately
                await supabase.auth.signOut();
                return;
              }
            } catch {
              await supabase.auth.signOut();
              return;
            }
          }

          // Email is verified, check MFA
          const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
            setNeedsMfa(true);
            await startMfaChallenge();
          } else {
            navigate("/");
          }
        }, 0);
      }
    });

    // THEN check for existing session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        const provider = session.user.app_metadata?.provider;
        const isOAuthUser = provider && provider !== 'email';

        // OAuth users can proceed without our custom verification
        if (isOAuthUser) {
          const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
            setNeedsMfa(true);
            await startMfaChallenge();
          } else {
            navigate("/");
          }
          return;
        }

        // For email users, treat platform-confirmed email as verified (fallback)
        const isEmailConfirmed = Boolean(
          session.user.email_confirmed_at || (session.user as any).confirmed_at
        );

        if (!isEmailConfirmed) {
          try {
            const { data: verificationData, error: verificationError } = await supabase.functions.invoke(
              'custom-auth-email',
              {
                body: { action: 'check_email_verified', email: session.user.email }
              }
            );

            if (verificationError || !verificationData?.verified) {
              // Not verified - sign out
              await supabase.auth.signOut();
              return;
            }
          } catch {
            await supabase.auth.signOut();
            return;
          }
        }

        // Check if MFA is required
        const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
          setNeedsMfa(true);
          await startMfaChallenge();
        } else {
          navigate("/");
        }
      }
    };
    checkSession();

    return () => subscription.unsubscribe();
  }, [navigate, toast]);

  const startMfaChallenge = async () => {
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totpFactor = factors?.totp.find((f) => f.status === "verified");

      if (totpFactor) {
        setMfaFactorId(totpFactor.id);
        const { data: challenge, error } = await supabase.auth.mfa.challenge({
          factorId: totpFactor.id
        });

        if (error) throw error;
        setMfaChallengeId(challenge.id);
      }
    } catch (error) {







      // MFA challenge error handled silently
    }};const verifyResetToken = async (token: string) => {try {const { data, error } = await supabase.functions.invoke('custom-auth-email', { body: { action: 'verify_reset_token', token } });

      if (error) throw error;

      if (data.success) {
        setResetToken(token);
        setShowNewPasswordForm(true);
      } else {
        toast({
          title: t("authError"),
          description: data.error === 'token_expired' ? t("tokenExpired") : t("invalidResetLink"),
          variant: "destructive"
        });
        // Clear URL params
        window.history.replaceState(null, '', '/auth');
      }
    } catch (error) {
      // Token verification error - show user-friendly message
      toast({
        title: t("authError"),
        description: t("invalidResetLink"),
        variant: "destructive"
      });
      window.history.replaceState(null, '', '/auth');
    }
  };

  const handleResetPassword = async () => {
    try {
      resetSchema(t).parse({ email: resetEmail });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: t("authError"),
          description: error.errors[0].message,
          variant: "destructive"
        });
      }
      return;
    }

    setIsResetting(true);
    try {
      // Use custom password reset email
      const { error } = await supabase.functions.invoke('custom-auth-email', {
        body: { action: 'send_password_reset', email: resetEmail }
      });

      if (error) throw error;

      toast({
        title: t("resetEmailSent"),
        description: t("resetEmailDesc")
      });
      setShowResetPassword(false);
      setResetEmail("");
    } catch (error: any) {
      toast({
        title: t("authError"),
        description: t("resetError"),
        variant: "destructive"
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleNewPassword = async () => {
    if (newPassword.length < 6) {
      toast({
        title: t("authError"),
        description: t("passwordMinLength"),
        variant: "destructive"
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: t("authError"),
        description: t("passwordsDoNotMatch"),
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('custom-auth-email', {
        body: { action: 'reset_password', token: resetToken, newPassword }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: t("passwordResetSuccess"),
          description: t("passwordResetSuccessDesc")
        });
        setShowNewPasswordForm(false);
        setResetToken("");
        setNewPassword("");
        setConfirmPassword("");
        window.history.replaceState(null, '', '/auth');
      } else {
        throw new Error(data.error);
      }
    } catch (error: any) {
      toast({
        title: t("authError"),
        description: t("resetError"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin + "/auth"
      });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: t("authError"),
        description: t("googleSignInError"),
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const verifyMfa = async () => {
    if (mfaCode.length !== 6) {
      toast({
        title: t("authError"),
        description: t("mfaCodeError"),
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: mfaChallengeId,
        code: mfaCode
      });

      if (error) throw error;

      toast({
        title: t("loginSuccess"),
        description: t("welcomeBack")
      });
      navigate("/");
    } catch (error: any) {
      toast({
        title: t("authError"),
        description: t("mfaCodeWrong"),
        variant: "destructive"
      });
      // Start a new challenge
      await startMfaChallenge();
      setMfaCode("");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    try {
      authSchema(t).parse({ email, password });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: {email?: string;password?: string;} = {};
        error.errors.forEach((err) => {
          if (err.path[0] === "email") fieldErrors.email = err.message;
          if (err.path[0] === "password") fieldErrors.password = err.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) throw error;

        // Check if user has verified their email (fallback: platform-confirmed counts as verified)
        const signedInUser = data.session?.user;
        const isEmailConfirmed = Boolean(
          signedInUser?.email_confirmed_at || (signedInUser as any)?.confirmed_at
        );

        if (!isEmailConfirmed) {
          try {
            const { data: verificationData, error: verificationError } = await supabase.functions.invoke(
              'custom-auth-email',
              {
                body: { action: 'check_email_verified', email }
              }
            );

            if (verificationError || !verificationData?.verified) {
              // User hasn't verified - sign them out and show error
              await supabase.auth.signOut();
              toast({
                title: t('authError'),
                description: t('emailNotConfirmed'),
                variant: 'destructive'
              });
              setLoading(false);
              return;
            }
          } catch {
            await supabase.auth.signOut();
            toast({
              title: t('authError'),
              description: t('emailNotConfirmed'),
              variant: 'destructive'
            });
            setLoading(false);
            return;
          }
        }

        // Check if MFA is required
        const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
          setNeedsMfa(true);
          await startMfaChallenge();
          return;
        }

        toast({
          title: t("loginSuccess"),
          description: t("welcomeBack")
        });
      } else {
        // Sign up user (won't be confirmed yet)
        const { data: signUpData, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`
          }
        });
        if (error) throw error;

        // Send branded verification email via Resend
        // The default Lovable Cloud email hook also fires (can't disable from code),
        // so user may receive 2 emails until support configures the hook routing
        if (signUpData.user) {
          try {
            await supabase.functions.invoke('custom-auth-email', {
              body: {
                action: 'send_verification',
                email: email,
                userId: signUpData.user.id
              }
            });
          } catch (emailErr) {
            console.error('Branded verification email failed:', emailErr);
          }
        }

        toast({
          title: t("signupSuccess"),
          description: t("checkEmail")
        });
        return; // Don't redirect, wait for email verification
      }
    } catch (error: any) {
      let message = t("authError");
      const errMsg = error.message || "";
      if (errMsg.includes("Invalid login credentials")) {
        message = t("invalidCredentials");
      } else if (errMsg.includes("User already registered")) {
        message = t("userExists");
      } else if (errMsg.includes("Email not confirmed")) {
        message = t("emailNotConfirmed");
      } else if (errMsg.includes("leaked") || errMsg.includes("pwned") || errMsg.includes("breached") || errMsg.includes("HIBP") || (error.code === "weak_password" && errMsg.includes("password"))) {
        message = t("passwordBreached");
      }
      toast({
        title: t("authError"),
        description: message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // MFA Verification Screen
  if (needsMfa) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" dir={dir}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md">
          
          <div className="bg-card border border-border rounded-3xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">{t("twoFactorAuth")}</h1>
              <p className="text-muted-foreground mt-2">
                {t("enterAuthCode")}
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="mfaCode">{t("verificationCode")}</Label>
                <Input
                  id="mfaCode"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono h-14"
                  dir="ltr"
                  maxLength={6}
                  autoFocus />
                
              </div>

              <Button
                onClick={verifyMfa}
                disabled={loading || mfaCode.length !== 6}
                className="w-full h-12 text-lg font-medium">
                
                {loading ?
                <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> :

                t("verifyAndLogin")
                }
              </Button>

              <Button
                variant="ghost"
                onClick={async () => {
                  await supabase.auth.signOut();
                  setNeedsMfa(false);
                  setMfaCode("");
                }}
                className="w-full">
                
                {t("backToLogin")}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>);

  }

  // New Password Form (after clicking reset link)
  if (showNewPasswordForm) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" dir={dir}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md">
          
          <div className="bg-card border border-border rounded-3xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
                <KeyRound className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">{t("setNewPassword")}</h1>
              <p className="text-muted-foreground mt-2">
                {t("resetEmailDesc")}
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="newPassword">{t("newPassword")}</Label>
                <div className="relative">
                  <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`h-12 ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'}`}
                    dir="ltr" />
                  
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground ${isRTL ? 'left-3' : 'right-3'}`}>
                    
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("confirmNewPassword")}</Label>
                <div className="relative">
                  <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`h-12 ${isRTL ? 'pr-10' : 'pl-10'}`}
                    dir="ltr" />
                  
                </div>
              </div>

              <Button
                onClick={handleNewPassword}
                disabled={loading || !newPassword || !confirmPassword}
                className="w-full h-12 text-lg font-medium">
                
                {loading ?
                <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> :

                t("setNewPassword")
                }
              </Button>

              <Button
                variant="ghost"
                onClick={() => {
                  setShowNewPasswordForm(false);
                  setResetToken("");
                  setNewPassword("");
                  setConfirmPassword("");
                  window.history.replaceState(null, '', '/auth');
                }}
                className="w-full">
                
                {t("backToLogin")}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>);

  }

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center p-2 sm:p-4" dir={dir}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md">
        
        <div
          className="bg-card border border-border rounded-3xl shadow-xl"
          style={{ padding: cardPadding }}>
          
          {/* Logo */}
          <div className="text-center" style={{ marginBottom: sectionGap }}>
            <div
              className="mx-auto mb-2 sm:mb-4 rounded-2xl bg-primary flex items-center justify-center"
              style={{ width: logoBoxSize, height: logoBoxSize }}>
              
              <img
                src={appLogo}
                alt="EyeControl App"
                style={{ width: logoIconSize, height: logoIconSize }} />
              
            </div>
            <h1
              className="font-bold text-foreground"
              style={{ fontSize: titleFontSize }}>
              
              EyeControl App 
            </h1>
            <p
              className="text-muted-foreground"
              style={{ marginTop: "clamp(0.25rem, 1vh, 0.5rem)", fontSize: subtitleFontSize }}>
              
              {isLogin ? t("loginToAccount") : t("createAccount")}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col" style={{ gap: sectionGap }}>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">
                {t("emailLabel")}
              </Label>
              <div className="relative">
                <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className={`${isRTL ? 'pr-10' : 'pl-10'}`}
                  style={{ height: controlHeight }}
                  dir="ltr"
                  autoComplete="email" />
                
              </div>
              {errors.email &&
              <p className="text-sm text-destructive">{errors.email}</p>
              }
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">
                {t("passwordLabel")}
              </Label>
              <div className="relative">
                <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'}`}
                  style={{ height: controlHeight }}
                  dir="ltr"
                  autoComplete={isLogin ? "current-password" : "new-password"} />
                
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground ${isRTL ? 'left-3' : 'right-3'}`}>
                  
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password &&
              <p className="text-sm text-destructive">{errors.password}</p>
              }
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full text-base sm:text-lg font-medium"
              style={{ height: controlHeight }}>
              
              {loading ?
              <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> :

              <>
                  {isLogin ? t("login") :
                <>
                      <UserPlus className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t("signup")}
                    </>
                }
                </>
              }
            </Button>
          </form>

          {/* Divider */}
          <div className="relative" style={{ marginBlock: dividerMargin }}>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">{t("or")}</span>
            </div>
          </div>

          {/* Google Sign In */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full text-base sm:text-lg font-medium gap-3"
            style={{ height: controlHeight }}>
            
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              
            </svg>
            Google
          </Button>

          {/* Toggle */}
          <div className="text-center" style={{ marginTop: sectionGap }}>
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setErrors({});
              }}
              className="text-primary hover:underline font-medium text-sm sm:text-base">
              
              {isLogin ? `${t("noAccount")} ${t("signup")}` : `${t("haveAccount")} ${t("login")}`}
            </button>
          </div>

          {isLogin &&
          <button
            onClick={() => setShowResetPassword(true)}
            className="block w-full text-center text-xs sm:text-sm text-muted-foreground hover:text-primary"
            style={{ marginTop: "clamp(0.25rem, 1.2vh, 0.75rem)" }}>
            
              {t("forgotPassword")}
            </button>
          }

          {!isLogin &&
          <p className="mt-2 sm:mt-3 text-center text-xs sm:text-sm text-muted-foreground">
              {t("checkEmail")}
            </p>
          }

          {/* Links */}
          <div className="flex justify-center gap-4 text-xs sm:text-sm" style={{ marginTop: sectionGap }}>
            <a
              href="/legal/privacy"
              className="text-muted-foreground hover:text-primary hover:underline">
              
              {t("privacyPolicy")}
            </a>
            <a
              href="/guide"
              className="text-muted-foreground hover:text-primary hover:underline">
              
              {t("aboutApp")}

            </a>
          </div>

          {/* Language Toggle */}
          <div className="flex justify-center" style={{ marginTop: sectionGap }}>
            <button
              onClick={() => setLanguage(lang === "he" ? "en" : "he")}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/80 backdrop-blur-sm border border-border hover:bg-card transition-colors text-sm font-medium text-muted-foreground"
            >
              <Globe className="w-4 h-4" />
              <span>{lang === "he" ? "English" : "עברית"}</span>
            </button>
          </div>

        </div>

        {/* Reset Password Modal */}
        {showResetPassword && <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowResetPassword(false)}>
          
            <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm">
            
              <h2 className="text-xl font-bold text-foreground mb-2">{t("resetPassword")}</h2>
              <p className="text-muted-foreground text-sm mb-4">
                {t("resetEmailDesc")}
              </p>
              
              <div className="space-y-4">
                <div className="relative">
                  <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                  <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="email@example.com"
                  className={`${isRTL ? 'pr-10' : 'pl-10'}`}
                  style={{ height: controlHeight }}
                  dir="ltr" />
                
                </div>

                <div className="flex gap-2">
                  <Button
                  onClick={handleResetPassword}
                  disabled={isResetting || !resetEmail}
                  className="flex-1">
                  
                    {isResetting ?
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> :

                  t("sendResetEmail")
                  }
                  </Button>
                  <Button
                  variant="outline"
                  onClick={() => setShowResetPassword(false)}>
                  
                    {t("cancel")}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        }
      </motion.div>
    </div>);

}